import { useRouter as useCompatRouter } from 'next/compat/router';
import { useRouter as useAppRouter } from 'next/navigation';

import type { UrlObject } from 'url';

type CompatRouter = NonNullable<ReturnType<typeof useCompatRouter>>;
type PushArgs = Parameters<CompatRouter['push']>;
type SafeUrl = PushArgs[0];

const stringifyQuery = (query: UrlObject['query']) => {
	if (!query || typeof query === 'string') return query ?? '';

	const searchParams = new URLSearchParams();

	Object.entries(query).forEach(([key, value]) => {
		if (value === undefined) return;

		if (Array.isArray(value)) {
			value.forEach((item) => {
				if (item !== undefined) searchParams.append(key, String(item));
			});
			return;
		}

		searchParams.set(key, String(value));
	});

	return searchParams.toString();
};

const stringifyUrl = (url: SafeUrl) => {
	if (typeof url === 'string') return url;

	const pathname = url.pathname ?? '';
	const query = stringifyQuery(url.query);
	const hash = url.hash ?? '';

	return `${pathname}${query ? `?${query}` : ''}${hash}`;
};

export function useSafeRouter() {
	const compat = useCompatRouter();
	const app = useAppRouter();

	return {
		push: (...args: PushArgs) => {
			if (compat) return compat.push(...args);

			const [url, as] = args;
			return app.push(stringifyUrl(as ?? url));
		},
	};
}
